import type {
  OrgEmployeeDisplayKpis,
  OrganizationStatsRaw,
  UserSummaryRow,
  UserSummaryStats,
} from '../types/employeeAnalysis'
import {
  MONTH_NAMES,
  type PeriodFilter,
  isRecordInPeriod,
  getYearMonthKeysForFilter,
} from './projectAnalysisPeriod'

export function computeOrgKpisFromRaw(
  stats: OrganizationStatsRaw,
  filter: PeriodFilter,
): OrgEmployeeDisplayKpis | null {
  const { summary, yearly_breakdown, monthly_trends } = stats
  if (!summary) return null

  const yAll = !filter.year || filter.year === 'all'
  const fyAll = !filter.fy || filter.fy === 'all'
  if (yAll && fyAll) {
    return {
      totalUsers: summary.total_employees,
      totalHours: summary.total_hours,
      billableHours: summary.billable_hours,
      avgUtilization: summary.avg_utilization,
      lateEntryRate:
        summary.late_entry_rate != null ? summary.late_entry_rate : null,
      totalRecords: summary.total_entries,
      activeEmployees: summary.active_employees,
      inactiveEmployees: summary.inactive_employees,
      dateRange: summary.date_range,
      subtitle: 'All time',
    }
  }

  const trendList = monthly_trends || []
  const filtered = trendList.filter((m) =>
    isRecordInPeriod(m.year, m.month, filter),
  )

  if (filtered.length === 0) {
    const yOnly =
      filter.year &&
      filter.year !== 'all' &&
      yearly_breakdown &&
      yearly_breakdown[String(filter.year)]
    if (yOnly) {
      const th = yOnly.total_hours || 0
      const bh = yOnly.billable_hours || 0
      const ent = yOnly.entries_count || 0
      const late = yOnly.late_entries || 0
      return {
        totalUsers: yOnly.active_users ?? summary.total_employees,
        totalHours: th,
        billableHours: bh,
        avgUtilization: th > 0 ? Math.round((bh / th) * 10000) / 100 : 0,
        lateEntryRate: ent > 0 ? Math.round((late / ent) * 10000) / 100 : null,
        totalRecords: ent,
        activeEmployees: yOnly.active_users ?? 0,
        inactiveEmployees: 0,
        dateRange: summary.date_range,
        subtitle: `Calendar ${filter.year}`,
      }
    }
    const sub =
      filter.fy && filter.fy !== 'all'
        ? `FY ${filter.fy}${
            filter.q && filter.q !== 'all' ? ` ${filter.q}` : ''
          }`
        : filter.month && filter.month !== 'all'
          ? `${MONTH_NAMES[Number(filter.month) - 1]} ${filter.year}`
          : String(filter.year ?? '')
    return {
      totalUsers: 0,
      totalHours: 0,
      billableHours: 0,
      avgUtilization: 0,
      lateEntryRate: null,
      totalRecords: 0,
      activeEmployees: 0,
      inactiveEmployees: 0,
      dateRange: summary.date_range,
      subtitle: sub,
    }
  }

  const totalHours = filtered.reduce((s, m) => s + (m.total_hours || 0), 0)
  const billableHours = filtered.reduce((s, m) => s + (m.billable_hours || 0), 0)
  const totalRecords = filtered.reduce(
    (s, m) => s + (m.entries_count ?? 0),
    0,
  )
  const maxActive = Math.max(0, ...filtered.map((m) => m.active_users || 0))
  let subtitle = 'Period'
  if (filter.fy && filter.fy !== 'all')
    subtitle = `FY ${filter.fy}${
      filter.q && filter.q !== 'all' ? ` ${filter.q}` : ''
    }`
  else if (filter.year && filter.year !== 'all')
    subtitle =
      filter.month && filter.month !== 'all'
        ? `${MONTH_NAMES[Number(filter.month) - 1]} ${filter.year}`
        : `Calendar ${filter.year}`

  let lateEntryRate: number | null = null
  const yearsInPeriod = [...new Set(filtered.map((m) => m.year))].filter(
    Boolean,
  )
  if (yearly_breakdown && yearsInPeriod.length > 0) {
    let sumLate = 0
    let sumEntries = 0
    yearsInPeriod.forEach((y) => {
      const yd = yearly_breakdown[String(y)]
      if (yd) {
        sumLate += yd.late_entries ?? 0
        sumEntries += yd.entries_count ?? 0
      }
    })
    if (sumEntries > 0)
      lateEntryRate = Math.round((sumLate / sumEntries) * 10000) / 100
  }

  return {
    totalUsers: maxActive || summary.total_employees,
    totalHours,
    billableHours,
    avgUtilization:
      totalHours > 0 ? Math.round((billableHours / totalHours) * 10000) / 100 : 0,
    lateEntryRate,
    totalRecords,
    activeEmployees: maxActive,
    inactiveEmployees: 0,
    dateRange: summary.date_range,
    subtitle,
  }
}

export function computeStatusFilteredOrgKpis(
  userSummaryStats: UserSummaryStats,
  filter: PeriodFilter,
  statusFilter: 'active' | 'inactive',
): OrgEmployeeDisplayKpis | null {
  const wantActive = statusFilter === 'active'
  const ymKeys = getYearMonthKeysForFilter(filter)
  const byMonth = userSummaryStats.by_year_month

  if (ymKeys?.length && byMonth) {
    const agg = new Map<number, UserSummaryRow>()
    for (const ym of ymKeys) {
      const users = byMonth[ym]
      if (!Array.isArray(users)) continue
      for (const u of users) {
        const id = u.user_id
        const existing = agg.get(id)
        const total = (u.total_hours ?? 0) + (existing?.total_hours ?? 0)
        const billable =
          (u.billable_hours ?? 0) + (existing?.billable_hours ?? 0)
        const nonBillable =
          (u.non_billable_hours ?? 0) + (existing?.non_billable_hours ?? 0)
        const entries = (u.total_entries ?? 0) + (existing?.total_entries ?? 0)
        const late = (u.late_entries ?? 0) + (existing?.late_entries ?? 0)
        agg.set(id, {
          user_id: id,
          username: existing?.username ?? u.username,
          full_name: existing?.full_name ?? u.full_name,
          email: existing?.email ?? u.email,
          is_active: existing !== undefined ? existing.is_active : u.is_active,
          total_hours: Math.round(total * 100) / 100,
          billable_hours: Math.round(billable * 100) / 100,
          non_billable_hours: Math.round(nonBillable * 100) / 100,
          total_entries: entries,
          late_entries: late,
        })
      }
    }
    const filtered = Array.from(agg.values()).filter((u) => {
      const active = u.is_active === 1 || u.is_active === true
      return wantActive ? active : !active
    })
    const totalHours = filtered.reduce(
      (s, u) => s + (u.total_hours ?? 0),
      0,
    )
    const billableHours = filtered.reduce(
      (s, u) => s + (u.billable_hours ?? 0),
      0,
    )
    const totalEntries = filtered.reduce((s, u) => s + (u.total_entries ?? 0), 0)
    const lateEntries = filtered.reduce((s, u) => s + (u.late_entries ?? 0), 0)
    let subtitle =
      filter?.fy && filter.fy !== 'all'
        ? `FY ${filter.fy}${
            filter.q && filter.q !== 'all' ? ` ${filter.q}` : ''
          }`
        : filter?.year && filter.year !== 'all'
          ? filter.month && filter.month !== 'all'
            ? `${MONTH_NAMES[Number(filter.month) - 1]} ${filter.year}`
            : `Calendar ${filter.year}`
          : 'All time'
    subtitle = (wantActive ? 'Active · ' : 'Inactive · ') + subtitle
    return {
      totalUsers: filtered.length,
      totalHours,
      billableHours,
      avgUtilization:
        totalHours > 0
          ? Math.round((billableHours / totalHours) * 10000) / 100
          : 0,
      lateEntryRate:
        totalEntries > 0
          ? Math.round((lateEntries / totalEntries) * 10000) / 100
          : null,
      totalRecords: totalEntries,
      activeEmployees: filtered.filter(
        (u) => u.is_active === 1 || u.is_active === true,
      ).length,
      inactiveEmployees: filtered.filter(
        (u) => !(u.is_active === 1 || u.is_active === true),
      ).length,
      dateRange: null,
      subtitle,
    }
  }

  const users = userSummaryStats.users || []
  const filtered = users.filter((u) => {
    const active = u.is_active === 1 || u.is_active === true
    return wantActive ? active : !active
  })
  const totalHours = filtered.reduce((s, u) => s + (u.total_hours || 0), 0)
  const billableHours = filtered.reduce((s, u) => s + (u.billable_hours || 0), 0)
  const totalEntries = filtered.reduce((s, u) => s + (u.total_entries || 0), 0)
  const lateEntries = filtered.reduce((s, u) => s + (u.late_entries || 0), 0)
  const subtitle = (wantActive ? 'Active · ' : 'Inactive · ') + 'All time'
  return {
    totalUsers: filtered.length,
    totalHours,
    billableHours,
    avgUtilization:
      totalHours > 0
        ? Math.round((billableHours / totalHours) * 10000) / 100
        : 0,
    lateEntryRate:
      totalEntries > 0
        ? Math.round((lateEntries / totalEntries) * 10000) / 100
        : null,
    totalRecords: totalEntries,
    activeEmployees: filtered.filter(
      (u) => u.is_active === 1 || u.is_active === true,
    ).length,
    inactiveEmployees: filtered.filter(
      (u) => !(u.is_active === 1 || u.is_active === true),
    ).length,
    dateRange: null,
    subtitle,
  }
}

export function resolveOrgEmployeeDisplayKpis(
  orgStats: OrgEmployeeDisplayKpis | null,
  statusFiltered: OrgEmployeeDisplayKpis | null,
  statusFilter: 'all' | 'active' | 'inactive',
): OrgEmployeeDisplayKpis | null {
  if (statusFiltered != null) return statusFiltered
  if (!orgStats) return null
  if (statusFilter === 'active' || statusFilter === 'inactive') {
    return {
      ...orgStats,
      totalUsers:
        statusFilter === 'active'
          ? orgStats.activeEmployees
          : orgStats.inactiveEmployees,
      subtitle: orgStats.subtitle
        ? (statusFilter === 'active'
            ? `Active · ${orgStats.subtitle}`
            : `Inactive · ${orgStats.subtitle}`)
        : statusFilter === 'active'
          ? 'Active only'
          : 'Inactive only',
    }
  }
  return orgStats
}
